import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Esquimalt = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.esquimalt:About Esquimalt Paragraph 1">
            Esquimalt, a picturesque municipality in the Greater Victoria region
            of Vancouver Island, showcases the maritime allure and community
            vibrancy prevalent in{' '}
            <LinkText href={BcNeighbourhoodsLink}>
              cities and neighbourhoods in BC
            </LinkText>
            . Adjacent to Victoria, Esquimalt boasts stunning waterfront views
            along the Strait of Juan de Fuca and a notable naval presence with
            the Canadian Forces Base. Its history is preserved through sites
            like Fort Rodd Hill and Fisgard Lighthouse. Parks like Esquimalt
            Gorge and Macaulay Point offer recreational opportunities amidst
            scenic landscapes.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.esquimalt:About Esquimalt Paragraph 2">
            The municipality’s diverse population contributes to a culturally
            rich environment, complemented by local businesses and community
            services. Esquimalt’s ongoing waterfront development reflects its
            commitment to enhancing public spaces. With a blend of historical
            significance, coastal beauty, and urban convenience, Esquimalt
            captures the essence of coastal living within the heart of{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia (BC)
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,800 - $2,900',
          bed2: '$2,100 - $3,200',
          bed3: '$2,700 - $3,900'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.esquimalt:Apartment Hunting in Esquimalt Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Esquimalt
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Apartments for rent Esquimalt Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Esquimalt
              </LinkText>{' '}
              are a popular choice for singles, couples, and small families.
              These apartments range from older, more budget-friendly units to
              newer, luxury complexes. Many are conveniently located near
              amenities like shopping centers, restaurants, and the waterfront,
              making them ideal for those who appreciate urban conveniences and
              easy access to outdoor activities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Houses for rent Esquimalt Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Esquimalt
              </LinkText>{' '}
              are sought after by families and individuals looking for more
              space and privacy. These rentals often feature gardens and yards,
              providing a comfortable living environment for those who enjoy
              outdoor activities and gardening. The variety of houses includes
              both older, character homes with unique features and newer, modern
              constructions, catering to different tastes.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Townhouses for rent Esquimalt Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Esquimalt
              </LinkText>{' '}
              offer a blend of privacy and community living. These are
              particularly popular among small families, couples, or
              professionals. Townhouses often provide multiple levels of living
              space, along with small private yards or patios, and are typically
              part of managed complexes, offering a sense of community with a
              bit more space than apartments.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Basements for rent Esquimalt Paragraph">
              Basements for rent in Esquimalt provide an affordable housing
              option, especially appealing to students, young professionals, or
              those on a tighter budget. These units, part of larger homes,
              offer privacy and basic amenities at a lower cost. While they may
              have less natural light, they often provide a cozy and secluded
              living space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Condos for rent Esquimalt Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Esquimalt
              </LinkText>{' '}
              attract those looking for a maintenance-free lifestyle with
              additional amenities. These rentals, ranging from older,
              well-maintained buildings to modern developments, are popular
              among young professionals, retirees, or small families. Condos
              often offer facilities like fitness centers, communal gardens, or
              social spaces.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Rooms for rent Esquimalt Paragraph">
              <LinkText href={links.room}>Rooms for rent in Esquimalt</LinkText>{' '}
              are the most economical choice, typically preferred by students,
              seasonal workers, or individuals seeking flexible living
              arrangements. This rental option involves sharing common areas
              like kitchens and living rooms with other occupants, providing a
              social, communal living experience.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Short term rentals Esquimalt Paragraph">
              Esquimalt has plenty of work opportunities and excellent academic
              institutions, making it a popular place for those renting on a
              short-term basis.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> are
              exceedingly popular in most cities, particularly during the summer
              months and at the beginning of school semesters. The added
              flexibility is an appealing prospect for many renters, and a key
              factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.esquimalt:Cheap rentals Esquimalt Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Esquimalt
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Esquimalt, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.esquimalt:Living in Esquimalt Paragraph">
            Living in Esquimalt, British Columbia, offers a unique blend of
            coastal beauty and community charm. Located on the southern tip of
            Vancouver Island, Esquimalt is surrounded by stunning ocean views,
            picturesque beaches, and tranquil parks. The town’s close-knit
            community is known for its warm and welcoming atmosphere, providing
            a wide array of amenities for both residents and visitors. Whether
            you’re an outdoor enthusiast seeking adventure on the water or a
            culture buff exploring local art and history, Esquimalt has
            something for everyone. With its safe and peaceful environment,
            Esquimalt offers an ideal place to call home for those seeking a
            serene coastal lifestyle.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.esquimalt:Living in Esquimalt Paragraph 2">
            If you’re not sure that Esquimalt is the right city for you, check
            out some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.esquimalt:Popular Neighbourhoods Esquimalt Paragraph">
          Like any city, Esquimalt is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Old Esquimalt, Saxe Point, Espinola Park, Gorge Vale, Rock
          Heights, Archie Browning Sports Centre, Admirals Walk, and Highrock
          Park. For more info about Esquimalt, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.esquimalt:Local Landmarks Esquimalt Paragraph">
          Esquimalt, BC, is a captivating region that is home to a plethora of
          extraordinary landmarks, each offering a glimpse into the area’s
          natural beauty and historical significance. One such landmark is the
          Esquimalt Lagoon, a stunning coastal stretch boasting picturesque
          views and abundant wildlife. The historic Point Ellice House and
          Gardens provides a fascinating insight into the region’s past, with
          its well-preserved Victorian-era architecture and meticulously
          landscaped gardens. For those seeking outdoor adventures, the West Bay
          Walkway offers a scenic waterfront trail, perfect for leisurely
          strolls or bike rides. Additionally, the CFB Esquimalt Naval &
          Military Museum showcases the area’s rich naval history and celebrates
          the contributions of the Canadian Armed Forces. These landmarks
          contribute to the allure of Esquimalt, making it a must-visit
          destination for both locals and tourists alike.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Esquimalt
